import { styled } from 'styled-components'

import {
  Colors,
  pxToRem,
  Shadows,
  Typography,
} from '@b-stock/fe-core/design-system'

import {
  CONTROL_HEIGHT,
  DIVIDER_SPACING,
  DIVIDER_WIDTH,
  GAP_WIDTH,
} from '../getActionPanelWidth'
import { PanelIconButton } from '../ui'

/**
 * The panel's right half.
 *
 * The left border is the divider between the two halves. It is on the menu
 * rather than a sibling element so it appears exactly when the menu does — an
 * empty registry leaves the form alone in the cell, with nothing to divide.
 *
 * It stands exactly as tall as the controls either side of it. The prototype
 * insets it from the row borders rather than running it the full height, so it
 * reads as a separator between two groups of controls rather than as another
 * line in the grid.
 */
export const Menu = styled.div`
  align-items: center;
  border-left: ${DIVIDER_WIDTH}px solid ${Colors.Semantic.Neutral.medium_grey};
  height: ${CONTROL_HEIGHT}px;
  display: flex;
  /* Never give up width to the form: the column is sized to fit these. */
  flex-shrink: 0;
  gap: ${GAP_WIDTH}px;
  padding-left: ${DIVIDER_SPACING}px;
`

/**
 * Disabled is `aria-disabled`, not the `disabled` attribute; it still responds.
 */
export const IconButton = styled(PanelIconButton)`
  &[aria-disabled='true'] {
    color: ${Colors.Semantic.BStock.disabled};
    cursor: default;

    &:hover {
      border-color: ${Colors.Semantic.BStock.default.darkBackground};
      border-style: dashed;
      color: ${Colors.Semantic.BStock.default.darkBackground};
    }
  }
`

/**
 * `visibility` rather than unmounting when the row it belongs to is scrolled
 * away: the panel keeps its place against its trigger and comes back with it.
 */
export const DropdownPanel = styled.div<{ $isRowVisible: boolean }>`
  ${Shadows.Popovers}

  background: ${Colors.Semantic.Neutral.white};
  border: ${pxToRem(1)} solid ${Colors.Semantic.Neutral.medium_grey};
  border-radius: 0.25rem;
  display: flex;
  flex-direction: column;
  margin: 0.25rem 0;
  max-width: 17rem;
  padding: 0.25rem;
  visibility: ${(o) => (o.$isRowVisible ? 'visible' : 'hidden')};
`

/** A statusMessage response to a click, anchored to the clicked control. */
export const StatusPanel = styled(DropdownPanel)`
  ${Typography.Body3}
  padding: 0.5rem 0.75rem;
`

export const RowLabel = styled.span`
  ${Typography.Body3}
`

export const RowDescription = styled.span`
  ${Typography.Body4}
  color: ${Colors.Semantic.Neutral.dark_grey};
`

export const RowText = styled.span`
  display: flex;
  flex-direction: column;
`

export const DropdownRow = styled.button`
  align-items: center;
  background: none;
  border: none;
  color: ${Colors.Semantic.Neutral.black};
  cursor: pointer;
  display: flex;
  gap: 0.75rem;
  padding: ${pxToRem(10)} 0.75rem;
  text-align: left;

  svg {
    color: ${Colors.Semantic.BStock.default.lightBackground};
    font-size: 1rem;
  }

  &:hover {
    background: ${Colors.Semantic.BStock.backgroundSelected};
  }

  &[aria-disabled='true'] {
    color: ${Colors.Semantic.BStock.disabled};
    cursor: default;

    &:hover {
      background: ${Colors.Semantic.Neutral.light_grey};
    }

    svg,
    ${RowDescription} {
      color: ${Colors.Semantic.BStock.disabled};
    }
  }
`
